import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Table, Button, Tag, Modal, Form, Input, Select, DatePicker, Row, Col,
  Popconfirm, Typography, Descriptions, EmptyState, message, TimePicker, type TagTone,
} from '../../ui';
import { Plus, Check, X } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { formatIstTime } from '../../lib/time';

export const REG_TYPE_LABEL: Record<string, string> = {
  MISSING_CHECK_IN: 'Forgot check-in', MISSING_CHECK_OUT: 'Forgot check-out', MISSING_BOTH: 'Forgot both',
};
export const REG_STATUS_COLOR: Record<string, TagTone> = {
  PENDING: 'gold', APPROVED: 'green', REJECTED: 'red', CANCELLED: 'neutral',
};
const DAY_TYPE = { PRESENT: 'green', HALF_DAY: 'gold' } as Record<string, TagTone>;

const requestedCell = (r: any) => (
  <div style={{ fontSize: 12 }}>
    {r.requestedCheckInAt && <div>In: {formatIstTime(r.requestedCheckInAt)}</div>}
    {r.requestedCheckOutAt && <div>Out: {formatIstTime(r.requestedCheckOutAt)}</div>}
    <Tag tone={DAY_TYPE[r.requestedStatus] ?? 'neutral'}>{r.requestedStatus}</Tag>
  </div>
);

// ── SP side: raise + track your own requests ──────────────────────────────────
export function MyRegularizationsCard() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: mine, isLoading } = useQuery({
    queryKey: ['reg-mine'],
    queryFn: () => request<any[]>({ url: '/attendance-regularizations/mine' }),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['reg-mine'] });
    qc.invalidateQueries({ queryKey: ['my-day'] });
  };
  const cancelMut = useMutation({
    mutationFn: (id: string) => request({ url: `/attendance-regularizations/${id}/cancel`, method: 'POST' }),
    onSuccess: () => { message.success('Request withdrawn'); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const cols = [
    { title: 'Date', dataIndex: 'logDate', width: 110 },
    { title: 'Missed', dataIndex: 'type', render: (v: string) => REG_TYPE_LABEL[v] ?? v },
    { title: 'Requested', render: (_: any, r: any) => requestedCell(r) },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag tone={REG_STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag> },
    { title: 'Reviewer', render: (_: any, r: any) => r.reviewedByName ?? '—' },
    { title: 'Note', dataIndex: 'reviewNote', render: (v: any) => v ?? '—', ellipsis: true },
    {
      title: '', width: 90, render: (_: any, r: any) => r.status === 'PENDING'
        ? <Popconfirm title="Withdraw this request?" onConfirm={() => cancelMut.mutate(String(r.id))}><Button size="sm" variant="danger">Withdraw</Button></Popconfirm>
        : null,
    },
  ];

  return (
    <Card title="Attendance regularization"
      extra={<Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setOpen(true)}>Request</Button>}>
      <Typography.Paragraph type="secondary" style={{ marginTop: -4, fontSize: 12 }}>
        Forgot to punch in or out? Request your manager to mark that day on your behalf (last 7 days).
      </Typography.Paragraph>
      <Table rowKey="id" size="small" loading={isLoading} dataSource={mine ?? []} columns={cols as any}
        pagination={{ defaultPageSize: 5, showSizeChanger: true }}
        locale={{ emptyText: <EmptyState title="No regularization requests" /> }} />
      <RequestRegularizationModal open={open} onClose={() => { setOpen(false); refresh(); }} />
    </Card>
  );
}

function RequestRegularizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const type = Form.useWatch('type', form);
  const needIn = type === 'MISSING_CHECK_IN' || type === 'MISSING_BOTH';
  const needOut = type === 'MISSING_CHECK_OUT' || type === 'MISSING_BOTH';

  const save = useMutation({
    mutationFn: (vals: any) => {
      const combine = (t: any) => t ? dayjs(vals.logDate).hour(t.hour()).minute(t.minute()).second(0).toISOString() : undefined;
      const isIn = vals.type === 'MISSING_CHECK_IN' || vals.type === 'MISSING_BOTH';
      const isOut = vals.type === 'MISSING_CHECK_OUT' || vals.type === 'MISSING_BOTH';
      return request({
        url: '/attendance-regularizations', method: 'POST', data: {
          logDate: vals.logDate.format('YYYY-MM-DD'),
          type: vals.type,
          checkInAt: isIn ? combine(vals.checkInTime) : undefined,
          checkOutAt: isOut ? combine(vals.checkOutTime) : undefined,
          status: vals.status,
          reason: vals.reason,
        },
      });
    },
    onSuccess: () => { message.success('Requested — your manager will review it'); form.resetFields(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to submit'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Request attendance regularization"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Submit request</Button>
      </>}>
      <Typography.Paragraph type="secondary" style={{ marginTop: -6 }}>
        Only the last 7 days can be regularized — older days stay Absent.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        initialValues={{ type: 'MISSING_BOTH', status: 'PRESENT', logDate: dayjs().subtract(1, 'day') }}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="logDate" label="Date" rules={[{ required: true }]}>
              <DatePicker className="w-full" disabledDate={(d) => d && (d.isAfter(dayjs(), 'day') || d.isBefore(dayjs().subtract(7, 'day'), 'day'))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="status" label="Day type" rules={[{ required: true }]}>
              <Select options={[{ value: 'PRESENT', label: 'Present (full day)' }, { value: 'HALF_DAY', label: 'Half day' }]} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="type" label="What did you miss?" rules={[{ required: true }]}>
          <Select options={[
            { value: 'MISSING_BOTH', label: 'Forgot both check-in & check-out' },
            { value: 'MISSING_CHECK_IN', label: 'Forgot check-in' },
            { value: 'MISSING_CHECK_OUT', label: 'Forgot check-out' },
          ]} />
        </Form.Item>
        <Row gutter={12}>
          {needIn && <Col span={12}><Form.Item name="checkInTime" label="Check-in time" rules={[{ required: true, message: 'Enter check-in time' }]}><TimePicker format="hh:mm A" style={{ width: '100%' }} /></Form.Item></Col>}
          {needOut && <Col span={12}><Form.Item name="checkOutTime" label="Check-out time" rules={[{ required: true, message: 'Enter check-out time' }]}><TimePicker format="hh:mm A" style={{ width: '100%' }} /></Form.Item></Col>}
        </Row>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'A reason is required' }]}>
          <Input.TextArea rows={2} placeholder="e.g. Phone died, was at a retailer site, network issue…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Manager side: review the team's pending requests ──────────────────────────
export function RegularizationReviewCard() {
  const [reviewFor, setReviewFor] = useState<any | null>(null);
  const { data: team, isLoading } = useQuery({
    queryKey: ['reg-team'],
    // Only PENDING — the card is a review queue, matching the nav badge.
    queryFn: () => request<any[]>({ url: '/attendance-regularizations', params: { status: 'PENDING' } }),
  });

  const cols = [
    { title: 'Date', dataIndex: 'logDate', width: 110 },
    { title: 'Staff', render: (_: any, r: any) => <div><div style={{ fontWeight: 500 }}>{r.staffName ?? `#${r.userId}`}</div>{r.staffRole && <div style={{ fontSize: 11, color: '#999' }}>{r.staffRole}</div>}</div> },
    { title: 'Missed', dataIndex: 'type', render: (v: string) => REG_TYPE_LABEL[v] ?? v },
    { title: 'Requested', render: (_: any, r: any) => requestedCell(r) },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: '', width: 110, render: (_: any, r: any) => <Button size="sm" variant="primary" icon={<Check size={16} />} onClick={() => setReviewFor(r)}>Review</Button> },
  ];

  return (
    <Card title="Requests to review">
      <Table rowKey="id" size="small" loading={isLoading} dataSource={team ?? []} columns={cols as any}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        locale={{ emptyText: <EmptyState title="No pending regularization requests" /> }} />
      <ReviewRegularizationModal req={reviewFor} onClose={() => setReviewFor(null)} />
    </Card>
  );
}

function ReviewRegularizationModal({ req, onClose }: { req: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>('PRESENT');
  const [checkInTime, setCheckInTime] = useState<Dayjs | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<Dayjs | null>(null);
  const [note, setNote] = useState('');

  // Seed from the request as soon as it changes (no flash; no dependence on open animation).
  useEffect(() => {
    if (!req) return;
    setStatus(req.requestedStatus ?? 'PRESENT');
    setCheckInTime(req.requestedCheckInAt ? dayjs(req.requestedCheckInAt) : null);
    setCheckOutTime(req.requestedCheckOutAt ? dayjs(req.requestedCheckOutAt) : null);
    setNote('');
  }, [req?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reviewMut = useMutation({
    mutationFn: (approve: boolean) => {
      const combine = (t: Dayjs | null) => (t && req) ? dayjs(req.logDate).hour(t.hour()).minute(t.minute()).second(0).toISOString() : undefined;
      return request({
        url: `/attendance-regularizations/${req.id}/review`, method: 'POST',
        data: approve
          ? { approve: true, status, checkInAt: combine(checkInTime), checkOutAt: combine(checkOutTime) }
          : { approve: false, note },
      });
    },
    onSuccess: (_d, approve) => {
      message.success(approve ? 'Approved — attendance marked' : 'Request rejected');
      qc.invalidateQueries({ queryKey: ['reg-team'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={!!req} onOpenChange={(o) => { if (!o) onClose(); }} title={req ? `Review · ${req.staffName ?? `#${req.userId}`} · ${req.logDate}` : 'Review'}
      footer={[
        <Button key="reject" variant="danger" icon={<X size={16} />} loading={reviewMut.isPending}
          onClick={() => { if (!note.trim()) { message.error('Add a note explaining the rejection'); return; } reviewMut.mutate(false); }}>Reject</Button>,
        <Button key="approve" variant="primary" icon={<Check size={16} />} loading={reviewMut.isPending}
          onClick={() => reviewMut.mutate(true)}>Approve &amp; mark</Button>,
      ]}>
      {req && (
        <>
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Staff">{req.staffName ?? `#${req.userId}`}{req.staffRole ? ` · ${req.staffRole}` : ''}</Descriptions.Item>
            <Descriptions.Item label="Date">{req.logDate}</Descriptions.Item>
            <Descriptions.Item label="Missed">{REG_TYPE_LABEL[req.type] ?? req.type}</Descriptions.Item>
            <Descriptions.Item label="Reason">{req.reason}</Descriptions.Item>
          </Descriptions>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Adjust if needed, then approve to mark the attendance:</Typography.Text>
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col span={8}><div style={{ fontSize: 12, marginBottom: 4 }}>Day type</div>
              <Select className="w-full" value={status} onChange={setStatus} options={[{ value: 'PRESENT', label: 'Present' }, { value: 'HALF_DAY', label: 'Half day' }]} /></Col>
            <Col span={8}><div style={{ fontSize: 12, marginBottom: 4 }}>Check-in</div>
              <TimePicker format="hh:mm A" style={{ width: '100%' }} value={checkInTime} onChange={setCheckInTime} /></Col>
            <Col span={8}><div style={{ fontSize: 12, marginBottom: 4 }}>Check-out</div>
              <TimePicker format="hh:mm A" style={{ width: '100%' }} value={checkOutTime} onChange={setCheckOutTime} /></Col>
          </Row>
          <div style={{ fontSize: 12, margin: '12px 0 4px' }}>Note <span style={{ color: '#999' }}>(required to reject)</span></div>
          <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional on approve; required on reject" />
        </>
      )}
    </Modal>
  );
}
